import { Drawer, Card, CardHeader, CardContent, Grid, Box, Button, useMediaQuery, Tooltip, ListItem, List, Divider, ListItemText, ListItemIcon, Typography, Stack } from "@mui/material";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { FC, useCallback, useState } from "react";
import ReadonlyField from "@/components/input/ReadonlyField";
import { DataAccessTokenDto } from "@/gen/api/encrypt";
import { autoCopyTextFieldProps } from "@/utils/clipboard";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { useDeleteWithConfirmController, useListContext, useRecordContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Confirm from "@/components/widgets/Confirm";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATA_ACCESS_TOKENS } from "@/constants";
import { useSelector } from "react-redux";
import { getUserProfile } from "@/reducers/userProfile";



const resource = 'settings/encryption/tokens'

export interface DataAccessTokenDetailProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const DataAccessTokenDetail: FC<DataAccessTokenDetailProps> = ({
  open,
  setOpen,
}) => {
  const handleClose = (): void => {
    setOpen(false);
  };
  const { refetch } = useListContext()
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const now = new Date().toISOString()
  const tokenDto : DataAccessTokenDto&RecPatch = useRecordContext()
  const expirationReached = !!tokenDto && new Date(tokenDto.expirationTime || 0).toISOString() < now
  const invalidated = !!tokenDto &&
    (tokenDto.tokenInvalidated || tokenDto.keyInvalidated || expirationReached);
    
  const delHandlers = useDefaultSavedHandlers('Invalidate Data Access Token');
  delHandlers.onSuccess.merge(useCallback(() => { setOpen(false) }, [setOpen]))
  delHandlers.onSuccess.merge(refetch)

  const hasManagePermit = useHasPermit([MANAGE_DATA_ACCESS_TOKENS])
  const user = useSelector(getUserProfile)
  const showInvalidateBtn = tokenDto && !invalidated && 
    (hasManagePermit || user?.userId === tokenDto.consumerUserId);
  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({
    resource: showInvalidateBtn ? resource : 'noop',
    record: (!invalidated ? tokenDto : {}) as any,
    ...delHandlers,
  })

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      {tokenDto &&
      
        <Card sx={{ overflowY: 'auto', mb: 4, }} >
          <CardHeader title="Data Access Token Info" />
          <CardContent>
            <Grid container spacing={4} >
              <DataAccessTokenDetailInternal tokenDto={tokenDto} expirationReached={expirationReached} invalidated={invalidated} />
            </Grid>
          </CardContent>
          <Stack direction="row" justifyContent="space-between" padding={2}>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleClose}
            >
              Close
            </Button>
            {showInvalidateBtn &&
              <>
                <Button color="error" variant="contained" onClick={handleDialogOpen}>
                  Invalidate Token
                </Button>
                <Confirm
                  isOpen={openConfirm}
                  title={"Invalidate Data Access Token"}
                  loading={loading}
                  onConfirm={handleDelete}
                  onClose={(e:any) => {
                    handleDialogClose(e); 
                  }}
                  confirm="Confirm"
                  cancel="Cancel"
                  content={(
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ ml: 1 }}
                        >
                          Please confirm whether to invalidate this
                          "{tokenDto?.tokenName}"
                          Data Access Token.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </>}
          </Stack>
        </Card>
      }

    </Drawer>
  )
}

export default DataAccessTokenDetail;


export const DataAccessTokenDetailInternal : FC<{tokenDto:DataAccessTokenDto, expirationReached:boolean, invalidated:boolean}> = ({
  tokenDto,
  expirationReached,
  invalidated,
}) => {
  const format = useUserTimeFormat()
  const [copied, setCopied] = useState(false)

  return (
    <>
      <Grid item xs={12} >
        <List sx={{ '& li.MuiListItem-root.sub div.MuiListItemText-root': { textAlign: 'end' } }}>
          <ListItem>
            <ListItemIcon>
              <ConfirmationNumberIcon color={invalidated ? "error" : "success"} />
            </ListItemIcon>
            <ListItemText 
              sx={{ justifyContent: 'space-between' }}
              primary={
                <>
                  <b>{tokenDto.tokenName}</b>
                  {!expirationReached && invalidated &&
                    <Typography variant="caption" color="GrayText" sx={{ float: 'right' }}>{
                      tokenDto.keyInvalidated ? "Key Retired" : "Token Invalidated"
                    }</Typography>
                  }
                </>
              } 
            />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Target Database
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.databaseName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Lifetime
              </Typography>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="body2" >
                  {format(tokenDto.createdTime || 0, 'Pp')}{" - "}
                  {format(tokenDto.expirationTime || 0, 'Pp')}
                </Typography>
              } 
            />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Requested By
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.requestUserName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Specified Consumer
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.consumerUserName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Data Access Roles
              </Typography>
            </ListItemIcon>
            <ListItemText primary={(tokenDto.dataAccessRoles || '').split(',').join(', ')} />
          </ListItem>
          <Divider />
        </List>
      </Grid>
      <Grid item xs={12}>
        {tokenDto.token &&
          <Tooltip title={copied ? 'Copied' : 'Click to Copy'}>
            <Box>
              <ReadonlyField 
                id="data-access-token-generated"
                label="Token Content"
                value={tokenDto.token || ''}
                {...autoCopyTextFieldProps(
                  () => setCopied(true), 
                  () => setTimeout(() => setCopied(false), 500)
                )}
                {...{ inputProps: { tabIndex: 0 }}}
              />
            </Box>
          </Tooltip>}
      </Grid>
    </>
  )
}
